import { EMPTY_STRING } from "@regrello/core-utils";
import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";

import { RegrelloFormFieldTextMultiline } from "../../../../formFields/RegrelloFormFieldTextMultiline";
import type { TextFieldPluginFrontendValue } from "./types";

export interface RegrelloEditableCellTextProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /**
   * A callback used to format the value for display in the cell. If provided, this function will be
   * invoked to format the value before it is displayed in the cell editor.
   *
   * This can be used, for example, to parse the email string out of a Party object that is
   * (unfortunately) shoved into this component at runtime for external users in webform with a
   * party field.
   */
  formatValue?: (value: unknown) => string;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: TextFieldPluginFrontendValue) => void;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: TextFieldPluginFrontendValue;
}

/**
 * An editable cell in which the user can input a phone number. Intended for use in
 * `RegrelloDataGridV2`.
 */
export function RegrelloEditableCellText({
  formatValue,
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellTextProps>) {
  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersioIn is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({
    api,
    eventKey,
    suppressKeyAltEnter: true,
    suppressKeyCmdEnter: true,
  });

  const { inputRef } = useDataGridV2FirstKeystroke({
    eventKey,
    onValueChange,
    parseEventKey: (ek) => ek,
  });

  const formattedValue = formatValue != null ? formatValue(value) : value;

  return (
    <div style={{ width: columnWidth }}>
      <RegrelloFormFieldTextMultiline
        autoFocus={true}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        minimumRowCount={1}
        onBlur={() => {
          api.stopEditing();
        }}
        onChange={(event) => {
          const value = event.target.value;
          onValueChange(value);
        }}
        onKeyDownCapture={onKeyDownCapture}
        size="x-small"
        textareaClassName="my-1"
        value={formattedValue ?? EMPTY_STRING}
      />
    </div>
  );
}
